import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productosApi } from '@/services/productosApi';
import { controladoresApi } from '@/services/controladoresApi';
import { pantallaModularApi } from '@/services/pantallaModularApi';
import { toast } from 'sonner';
import type { ProductoCreateRequest, TipoProducto, ProductoDto } from '@/types/producto';
import type { ControladorDto, ControladorCreateRequest, TipoControlador } from '@/types/controlador';
import type { PantallaModularDto, PantallaModularCreateRequest } from '@/types/pantallaModular';
import { Plus, Pencil, Trash2, Image } from 'lucide-react';
import { resolveImageUrl } from '@/utils/formatters';
import { StockBadge } from '@/components/shared/StockBadge';
import { FuentesSection } from '@/components/products/FuentesSection';
import { TarjetasReceptorasSection } from '@/components/products/TarjetasReceptorasSection';
import { OtrosItemsSection } from '@/components/products/OtrosItemsSection';

const emptyForm: ProductoCreateRequest = {
  codigo: '', nombre: '', tipo: 'INDOOR',
  anchoCm: 0, altoCm: 0, pixelPitch: 0,
};

export function Productos() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<TipoProducto | ''>('');
  const [form, setForm] = useState<ProductoCreateRequest>({ ...emptyForm });

  // Image upload state
  const [showImgModal, setShowImgModal] = useState(false);
  const [imgProducto, setImgProducto] = useState<ProductoDto | null>(null);
  const [imgCabinet, setImgCabinet] = useState<File | null>(null);
  const [imgModule, setImgModule] = useState<File | null>(null);
  const [previewCabinet, setPreviewCabinet] = useState<string>('');
  const [previewModule, setPreviewModule] = useState<string>('');
  const cabinetInputRef = useRef<HTMLInputElement>(null);
  const moduleInputRef = useRef<HTMLInputElement>(null);

  // Controlador state
  const [showCtrlModal, setShowCtrlModal] = useState(false);
  const [editingCtrlId, setEditingCtrlId] = useState<number | null>(null);
  const [ctrlForm, setCtrlForm] = useState<ControladorCreateRequest>({ nombre: '', puertos: 1, tipo: 'SINCRONO', capacidadPixel: null });
  const [showCtrlImgModal, setShowCtrlImgModal] = useState(false);
  const [imgCtrl, setImgCtrl] = useState<ControladorDto | null>(null);
  const [ctrlImgFile, setCtrlImgFile] = useState<File | null>(null);
  const [ctrlImgPreview, setCtrlImgPreview] = useState('');
  const ctrlImgInputRef = useRef<HTMLInputElement>(null);

  // Pantalla Modular state
  const emptyPmForm: PantallaModularCreateRequest = {
    codigo: '', nombre: '', tipo: 'INDOOR', anchoModuloMm: 0, altoModuloMm: 0, pixelPitch: 0,
  };
  const [showPmModal, setShowPmModal] = useState(false);
  const [editingPmId, setEditingPmId] = useState<number | null>(null);
  const [pmForm, setPmForm] = useState<PantallaModularCreateRequest>({ ...emptyPmForm });
  const [showPmImgModal, setShowPmImgModal] = useState(false);
  const [imgPm, setImgPm] = useState<PantallaModularDto | null>(null);
  const [pmImgFile, setPmImgFile] = useState<File | null>(null);
  const [pmImgPreview, setPmImgPreview] = useState('');
  const pmImgInputRef = useRef<HTMLInputElement>(null);

  const { data: productos = [], isLoading } = useQuery({
    queryKey: ['productos', filtroTipo],
    queryFn: () => productosApi.findAll(filtroTipo || undefined),
  });

  const { data: controladores = [], isLoading: ctrlLoading } = useQuery({
    queryKey: ['controladores'],
    queryFn: controladoresApi.findAll,
  });

  const { data: pantallasModulares = [], isLoading: pmLoading } = useQuery({
    queryKey: ['pantallas-modulares'],
    queryFn: pantallaModularApi.findAll,
  });

  const ctrlSaveMutation = useMutation({
    mutationFn: (data: ControladorCreateRequest) =>
      editingCtrlId ? controladoresApi.update(editingCtrlId, data) : controladoresApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controladores'] });
      setShowCtrlModal(false); setEditingCtrlId(null); setCtrlForm({ nombre: '', puertos: 1, tipo: 'SINCRONO', capacidadPixel: null });
      toast.success(editingCtrlId ? 'Controlador actualizado' : 'Controlador creado');
    },
    onError: () => toast.error('Error al guardar controlador'),
  });

  const ctrlDeleteMutation = useMutation({
    mutationFn: controladoresApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controladores'] });
      toast.success('Controlador eliminado');
    },
  });

  const ctrlUploadMutation = useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      controladoresApi.uploadImage(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controladores'] });
      setShowCtrlImgModal(false); setImgCtrl(null); setCtrlImgFile(null); setCtrlImgPreview('');
      toast.success('Imagen actualizada');
    },
    onError: () => toast.error('Error al subir imagen'),
  });

  const pmSaveMutation = useMutation({
    mutationFn: (data: PantallaModularCreateRequest) =>
      editingPmId ? pantallaModularApi.update(editingPmId, data) : pantallaModularApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pantallas-modulares'] });
      setShowPmModal(false); setEditingPmId(null); setPmForm({ ...emptyPmForm });
      toast.success(editingPmId ? 'Pantalla modular actualizada' : 'Pantalla modular creada');
    },
    onError: () => toast.error('Error al guardar pantalla modular'),
  });

  const pmDeleteMutation = useMutation({
    mutationFn: pantallaModularApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pantallas-modulares'] });
      toast.success('Pantalla modular eliminada');
    },
  });

  const pmUploadMutation = useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      pantallaModularApi.uploadImage(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pantallas-modulares'] });
      setShowPmImgModal(false); setImgPm(null); setPmImgFile(null); setPmImgPreview('');
      toast.success('Imagen actualizada');
    },
    onError: () => toast.error('Error al subir imagen'),
  });

  const openPmEdit = (pm: PantallaModularDto) => {
    setEditingPmId(pm.id);
    setPmForm({
      codigo: pm.codigo, nombre: pm.nombre, tipo: pm.tipo,
      anchoModuloMm: pm.anchoModuloMm, altoModuloMm: pm.altoModuloMm,
      profundidadModuloMm: pm.profundidadModuloMm, pesoModuloGramos: pm.pesoModuloGramos,
      pixelPitch: pm.pixelPitch, ledType: pm.ledType, resolucionModulo: pm.resolucionModulo,
      brilloNits: pm.brilloNits, consumoMaxW: pm.consumoMaxW, consumoPromW: pm.consumoPromW,
      refreshRate: pm.refreshRate,
    });
    setShowPmModal(true);
  };

  const openPmImg = (pm: PantallaModularDto) => {
    setImgPm(pm);
    setPmImgFile(null);
    setPmImgPreview(resolveImageUrl(pm.imagen) ?? '');
    setShowPmImgModal(true);
  };

  const openCtrlEdit = (c: ControladorDto) => {
    setEditingCtrlId(c.id);
    setCtrlForm({ nombre: c.nombre, puertos: c.puertos, tipo: c.tipo, capacidadPixel: c.capacidadPixel });
    setShowCtrlModal(true);
  };

  const openCtrlImg = (c: ControladorDto) => {
    setImgCtrl(c);
    setCtrlImgFile(null);
    setCtrlImgPreview(resolveImageUrl(c.imagen) ?? '');
    setShowCtrlImgModal(true);
  };

  const createMutation = useMutation({
    mutationFn: (data: ProductoCreateRequest) =>
      editingId ? productosApi.update(editingId, data) : productosApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productos'] });
      closeModal();
      toast.success(editingId ? 'Gabinete actualizado' : 'Gabinete creado');
    },
    onError: () => toast.error('Error al guardar gabinete'),
  });

  const deleteMutation = useMutation({
    mutationFn: productosApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productos'] });
      toast.success('Gabinete eliminado');
    },
  });

  const uploadImagesMutation = useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      productosApi.uploadImages(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productos'] });
      closeImgModal();
      toast.success('Imágenes actualizadas');
    },
    onError: () => toast.error('Error al subir imágenes'),
  });

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (p: ProductoDto) => {
    setEditingId(p.id);
    setForm({
      codigo: p.codigo, nombre: p.nombre, tipo: p.tipo,
      anchoCm: p.anchoCm, altoCm: p.altoCm, pixelPitch: p.pixelPitch,
      profundidadMm: p.profundidadMm, pesoKg: p.pesoKg,
      ledType: p.ledType, resolucionGabinete: p.resolucionGabinete,
      brilloNits: p.brilloNits, consumoMaxW: p.consumoMaxW,
      consumoPromW: p.consumoPromW, refreshRate: p.refreshRate,
      vidaUtil: p.vidaUtil, proteccionIp: p.proteccionIp,
    });
    setShowModal(true);
  };

  const openImgModal = (p: ProductoDto) => {
    setImgProducto(p);
    setImgCabinet(null);
    setImgModule(null);
    setPreviewCabinet(resolveImageUrl(p.imagenCabinet) ?? '');
    setPreviewModule(resolveImageUrl(p.imagenModule) ?? '');
    setShowImgModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const closeImgModal = () => {
    setShowImgModal(false);
    setImgProducto(null);
    setImgCabinet(null);
    setImgModule(null);
    setPreviewCabinet('');
    setPreviewModule('');
  };

  const handleFileChange = (
    file: File | null,
    type: 'cabinet' | 'module'
  ) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === 'cabinet') {
      setImgCabinet(file);
      setPreviewCabinet(url);
    } else {
      setImgModule(file);
      setPreviewModule(url);
    }
  };

  const handleUploadSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!imgProducto || (!imgCabinet && !imgModule)) {
      toast.error('Selecciona al menos una imagen');
      return;
    }
    const formData = new FormData();
    if (imgCabinet) formData.append('imagenCabinet', imgCabinet);
    if (imgModule) formData.append('imagenModule', imgModule);
    uploadImagesMutation.mutate({ id: imgProducto.id, formData });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    createMutation.mutate(form);
  };

  const updateField = (field: string, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Catálogo de Gabinetes</h2>
        <div className="flex gap-3">
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value as TipoProducto | '')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todos</option>
            <option value="INDOOR">Indoor</option>
            <option value="OUTDOOR">Outdoor</option>
          </select>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus size={16} />
            Nuevo Gabinete
          </button>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Imagen</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nombre</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Dimensiones</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Pixel Pitch</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : productos.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">No hay gabinetes</td></tr>
            ) : (
              productos.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {p.imagenCabinet ? (
                      <img
                        src={resolveImageUrl(p.imagenCabinet)!}
                        alt={p.nombre}
                        className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                        <Image size={16} className="text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{p.codigo}</td>
                  <td className="px-4 py-3 text-sm font-medium">{p.nombre}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      p.tipo === 'INDOOR' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {p.tipo}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{p.anchoCm}x{p.altoCm} cm</td>
                  <td className="px-4 py-3 text-sm">{p.pixelPitch}mm</td>
                  <td className="px-4 py-3"><StockBadge cantidad={p.cantidadTotal} desglose={p.stockPorAlmacen} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openImgModal(p)}
                        className="text-purple-500 hover:text-purple-700"
                        title="Gestionar imágenes"
                      >
                        <Image size={16} />
                      </button>
                      <button onClick={() => openEdit(p)} className="text-blue-600 hover:text-blue-800">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => deleteMutation.mutate(p.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : productos.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No hay gabinetes</p>
        ) : (
          productos.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex gap-3">
                {p.imagenCabinet ? (
                  <img
                    src={resolveImageUrl(p.imagenCabinet)!}
                    alt={p.nombre}
                    className="w-14 h-14 object-cover rounded-lg border border-gray-200 shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center shrink-0">
                    <Image size={20} className="text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium truncate">{p.nombre}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                      p.tipo === 'INDOOR' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {p.tipo}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 font-mono">{p.codigo}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{p.anchoCm}x{p.altoCm} cm · {p.pixelPitch}mm</p>
                  <div className="mt-1"><StockBadge cantidad={p.cantidadTotal} desglose={p.stockPorAlmacen} /></div>
                </div>
              </div>
              <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100 justify-end">
                <button onClick={() => openImgModal(p)} className="text-purple-500 hover:text-purple-700 p-1" title="Imágenes">
                  <Image size={18} />
                </button>
                <button onClick={() => openEdit(p)} className="text-blue-600 hover:text-blue-800 p-1" title="Editar">
                  <Pencil size={18} />
                </button>
                <button onClick={() => deleteMutation.mutate(p.id)} className="text-red-500 hover:text-red-700 p-1" title="Eliminar">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Image Upload Modal */}
      {showImgModal && imgProducto && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-1">Imágenes del Gabinete</h3>
            <p className="text-sm text-gray-500 mb-5">{imgProducto.nombre}</p>

            <form onSubmit={handleUploadSubmit} className="space-y-5">
              {/* Imagen Gabinete */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Imagen del Gabinete</p>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => cabinetInputRef.current?.click()}
                >
                  {previewCabinet ? (
                    <img
                      src={previewCabinet}
                      alt="Preview gabinete"
                      className="w-full max-h-40 object-contain rounded-lg"
                    />
                  ) : (
                    <>
                      <Image size={32} className="text-gray-300" />
                      <span className="text-sm text-gray-400">Haz clic para seleccionar imagen</span>
                    </>
                  )}
                  {previewCabinet && (
                    <span className="text-xs text-blue-600 hover:underline">Cambiar imagen</span>
                  )}
                </div>
                <input
                  ref={cabinetInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e.target.files?.[0] ?? null, 'cabinet')}
                />
              </div>

              {/* Imagen Módulo */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Imagen del Módulo</p>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => moduleInputRef.current?.click()}
                >
                  {previewModule ? (
                    <img
                      src={previewModule}
                      alt="Preview módulo"
                      className="w-full max-h-40 object-contain rounded-lg"
                    />
                  ) : (
                    <>
                      <Image size={32} className="text-gray-300" />
                      <span className="text-sm text-gray-400">Haz clic para seleccionar imagen</span>
                    </>
                  )}
                  {previewModule && (
                    <span className="text-xs text-blue-600 hover:underline">Cambiar imagen</span>
                  )}
                </div>
                <input
                  ref={moduleInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e.target.files?.[0] ?? null, 'module')}
                />
                <p className="text-xs text-gray-400 mt-1">Formatos: JPG, PNG, WebP. Máx. 5MB por imagen.</p>
              </div>

              <div className="flex gap-3 justify-end pt-1">
                <button
                  type="button"
                  onClick={closeImgModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploadImagesMutation.isPending || (!imgCabinet && !imgModule)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
                >
                  {uploadImagesMutation.isPending ? 'Subiendo...' : 'Guardar imágenes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ CONTROLADORES ═══ */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 mt-12">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Controladores / Procesadores</h2>
        <button
          onClick={() => { setEditingCtrlId(null); setCtrlForm({ nombre: '', puertos: 1, tipo: 'SINCRONO', capacidadPixel: null }); setShowCtrlModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Plus size={16} />
          Nuevo Controlador
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Imagen</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nombre</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Puertos</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {ctrlLoading ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : controladores.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">No hay controladores</td></tr>
            ) : (
              controladores.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {c.imagen ? (
                      <img src={resolveImageUrl(c.imagen)!} alt={c.nombre} className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                        <Image size={16} className="text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">{c.nombre}</td>
                  <td className="px-4 py-3 text-sm">{c.tipo === 'SINCRONO' ? 'Síncrono' : 'Asíncrono'}{c.capacidadPixel ? ` (${c.capacidadPixel}M px)` : ''}</td>
                  <td className="px-4 py-3 text-sm">{c.puertos}</td>
                  <td className="px-4 py-3"><StockBadge cantidad={c.cantidadTotal} desglose={c.stockPorAlmacen} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openCtrlImg(c)} className="text-purple-500 hover:text-purple-700" title="Imagen"><Image size={16} /></button>
                      <button onClick={() => openCtrlEdit(c)} className="text-blue-600 hover:text-blue-800"><Pencil size={16} /></button>
                      <button onClick={() => ctrlDeleteMutation.mutate(c.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards controladores */}
      <div className="md:hidden space-y-3 mb-4">
        {ctrlLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : controladores.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No hay controladores</p>
        ) : (
          controladores.map((c) => (
            <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex gap-3 items-center">
                {c.imagen ? (
                  <img src={resolveImageUrl(c.imagen)!} alt={c.nombre} className="w-12 h-12 object-cover rounded-lg border" />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded-lg border flex items-center justify-center"><Image size={18} className="text-gray-400" /></div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium">{c.nombre}</p>
                  <p className="text-xs text-gray-500">{c.tipo === 'SINCRONO' ? 'Síncrono' : 'Asíncrono'} | {c.puertos} puertos{c.capacidadPixel ? ` | ${c.capacidadPixel}M px` : ''}</p>
                  <div className="mt-1"><StockBadge cantidad={c.cantidadTotal} desglose={c.stockPorAlmacen} /></div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openCtrlImg(c)} className="text-purple-500"><Image size={16} /></button>
                  <button onClick={() => openCtrlEdit(c)} className="text-blue-600"><Pencil size={16} /></button>
                  <button onClick={() => ctrlDeleteMutation.mutate(c.id)} className="text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ═══ PANTALLAS MODULARES ═══ */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 mt-12">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Pantallas Modulares</h2>
        <button
          onClick={() => { setEditingPmId(null); setPmForm({ ...emptyPmForm }); setShowPmModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Plus size={16} />
          Nueva Pantalla Modular
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Imagen</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nombre</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Módulo (mm)</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Pixel Pitch</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pmLoading ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : pantallasModulares.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">No hay pantallas modulares</td></tr>
            ) : (
              pantallasModulares.map((pm) => (
                <tr key={pm.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {pm.imagen ? (
                      <img src={resolveImageUrl(pm.imagen)!} alt={pm.nombre} className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center"><Image size={16} className="text-gray-400" /></div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{pm.codigo}</td>
                  <td className="px-4 py-3 text-sm font-medium">{pm.nombre}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pm.tipo === 'INDOOR' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{pm.tipo}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{pm.anchoModuloMm}×{pm.altoModuloMm}</td>
                  <td className="px-4 py-3 text-sm">{pm.pixelPitch}mm</td>
                  <td className="px-4 py-3"><StockBadge cantidad={pm.cantidadTotal} desglose={pm.stockPorAlmacen} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openPmImg(pm)} className="text-purple-500 hover:text-purple-700" title="Imagen"><Image size={16} /></button>
                      <button onClick={() => openPmEdit(pm)} className="text-blue-600 hover:text-blue-800"><Pencil size={16} /></button>
                      <button onClick={() => pmDeleteMutation.mutate(pm.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards pantallas modulares */}
      <div className="md:hidden space-y-3 mb-4">
        {pmLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : pantallasModulares.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No hay pantallas modulares</p>
        ) : (
          pantallasModulares.map((pm) => (
            <div key={pm.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex gap-3 items-center">
                {pm.imagen ? (
                  <img src={resolveImageUrl(pm.imagen)!} alt={pm.nombre} className="w-12 h-12 object-cover rounded-lg border" />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded-lg border flex items-center justify-center"><Image size={18} className="text-gray-400" /></div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">{pm.nombre}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${pm.tipo === 'INDOOR' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{pm.tipo}</span>
                  </div>
                  <p className="text-xs text-gray-500 font-mono">{pm.codigo}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{pm.anchoModuloMm}×{pm.altoModuloMm} mm · P{pm.pixelPitch}</p>
                  <div className="mt-1"><StockBadge cantidad={pm.cantidadTotal} desglose={pm.stockPorAlmacen} /></div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openPmImg(pm)} className="text-purple-500"><Image size={16} /></button>
                  <button onClick={() => openPmEdit(pm)} className="text-blue-600"><Pencil size={16} /></button>
                  <button onClick={() => pmDeleteMutation.mutate(pm.id)} className="text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ═══ FUENTES DE ALIMENTACIÓN ═══ */}
      <FuentesSection />

      {/* ═══ TARJETAS RECEPTORAS ═══ */}
      <TarjetasReceptorasSection />

      {/* ═══ OTROS ÍTEMS ═══ */}
      <OtrosItemsSection />

      {/* Pantalla Modular Create/Edit Modal */}
      {showPmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">{editingPmId ? 'Editar Pantalla Modular' : 'Nueva Pantalla Modular'}</h3>
            <form onSubmit={(e) => { e.preventDefault(); pmSaveMutation.mutate(pmForm); }} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                  <input type="text" value={pmForm.codigo} onChange={(e) => setPmForm({ ...pmForm, codigo: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input type="text" value={pmForm.nombre} onChange={(e) => setPmForm({ ...pmForm, nombre: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select value={pmForm.tipo} onChange={(e) => setPmForm({ ...pmForm, tipo: e.target.value as 'INDOOR' | 'OUTDOOR' })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="INDOOR">Indoor</option>
                    <option value="OUTDOOR">Outdoor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pixel Pitch (mm)</label>
                  <input type="number" step="0.01" value={pmForm.pixelPitch || ''} onChange={(e) => setPmForm({ ...pmForm, pixelPitch: parseFloat(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ancho Módulo (mm)</label>
                  <input type="number" step="0.01" value={pmForm.anchoModuloMm || ''} onChange={(e) => setPmForm({ ...pmForm, anchoModuloMm: parseFloat(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alto Módulo (mm)</label>
                  <input type="number" step="0.01" value={pmForm.altoModuloMm || ''} onChange={(e) => setPmForm({ ...pmForm, altoModuloMm: parseFloat(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profundidad Módulo (mm)</label>
                  <input type="number" step="0.01" value={pmForm.profundidadModuloMm || ''} onChange={(e) => setPmForm({ ...pmForm, profundidadModuloMm: parseFloat(e.target.value) || undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Peso Módulo (gramos)</label>
                  <input type="number" step="0.01" value={pmForm.pesoModuloGramos || ''} onChange={(e) => setPmForm({ ...pmForm, pesoModuloGramos: parseFloat(e.target.value) || undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo LED</label>
                  <input type="text" value={pmForm.ledType || ''} onChange={(e) => setPmForm({ ...pmForm, ledType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resolución Módulo</label>
                  <input type="text" value={pmForm.resolucionModulo || ''} onChange={(e) => setPmForm({ ...pmForm, resolucionModulo: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" placeholder="ej: 64×64" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brillo (nits)</label>
                  <input type="number" value={pmForm.brilloNits || ''} onChange={(e) => setPmForm({ ...pmForm, brilloNits: parseInt(e.target.value) || undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Consumo Máx (W)</label>
                  <input type="number" step="0.01" value={pmForm.consumoMaxW || ''} onChange={(e) => setPmForm({ ...pmForm, consumoMaxW: parseFloat(e.target.value) || undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Consumo Prom (W)</label>
                  <input type="number" step="0.01" value={pmForm.consumoPromW || ''} onChange={(e) => setPmForm({ ...pmForm, consumoPromW: parseFloat(e.target.value) || undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Rate (Hz)</label>
                  <input type="number" value={pmForm.refreshRate || ''} onChange={(e) => setPmForm({ ...pmForm, refreshRate: parseInt(e.target.value) || undefined })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowPmModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={pmSaveMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  {pmSaveMutation.isPending ? 'Guardando...' : editingPmId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pantalla Modular Image Modal */}
      {showPmImgModal && imgPm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-1">Imagen de Pantalla Modular</h3>
            <p className="text-sm text-gray-500 mb-5">{imgPm.nombre}</p>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!pmImgFile) { toast.error('Selecciona una imagen'); return; }
              const fd = new FormData();
              fd.append('imagen', pmImgFile);
              pmUploadMutation.mutate({ id: imgPm.id, formData: fd });
            }} className="space-y-5">
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => pmImgInputRef.current?.click()}
              >
                {pmImgPreview ? (
                  <img src={pmImgPreview} alt="Preview" className="w-full max-h-40 object-contain rounded-lg" />
                ) : (
                  <><Image size={32} className="text-gray-300" /><span className="text-sm text-gray-400">Haz clic para seleccionar imagen</span></>
                )}
                {pmImgPreview && <span className="text-xs text-blue-600 hover:underline">Cambiar imagen</span>}
              </div>
              <input ref={pmImgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setPmImgFile(f); setPmImgPreview(URL.createObjectURL(f)); }
              }} />
              <div className="flex gap-3 justify-end pt-1">
                <button type="button" onClick={() => setShowPmImgModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={pmUploadMutation.isPending || !pmImgFile} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
                  {pmUploadMutation.isPending ? 'Subiendo...' : 'Guardar imagen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Controlador Create/Edit Modal */}
      {showCtrlModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">{editingCtrlId ? 'Editar Controlador' : 'Nuevo Controlador'}</h3>
            <form onSubmit={(e) => { e.preventDefault(); ctrlSaveMutation.mutate(ctrlForm); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input type="text" value={ctrlForm.nombre} onChange={(e) => setCtrlForm({ ...ctrlForm, nombre: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Puertos Ethernet</label>
                <input type="number" min={1} value={ctrlForm.puertos} onChange={(e) => setCtrlForm({ ...ctrlForm, puertos: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select value={ctrlForm.tipo} onChange={(e) => setCtrlForm({ ...ctrlForm, tipo: e.target.value as TipoControlador, capacidadPixel: e.target.value === 'SINCRONO' ? null : ctrlForm.capacidadPixel })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="SINCRONO">Síncrono</option>
                  <option value="ASINCRONO">Asíncrono</option>
                </select>
              </div>
              {ctrlForm.tipo === 'ASINCRONO' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacidad Píxeles (millones)</label>
                  <input type="number" step={0.1} min={0} value={ctrlForm.capacidadPixel ?? ''} onChange={(e) => setCtrlForm({ ...ctrlForm, capacidadPixel: parseFloat(e.target.value) || null })} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
              )}
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowCtrlModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={ctrlSaveMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  {ctrlSaveMutation.isPending ? 'Guardando...' : editingCtrlId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Controlador Image Modal */}
      {showCtrlImgModal && imgCtrl && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-1">Imagen del Controlador</h3>
            <p className="text-sm text-gray-500 mb-5">{imgCtrl.nombre}</p>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!ctrlImgFile) { toast.error('Selecciona una imagen'); return; }
              const fd = new FormData();
              fd.append('imagen', ctrlImgFile);
              ctrlUploadMutation.mutate({ id: imgCtrl.id, formData: fd });
            }} className="space-y-5">
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => ctrlImgInputRef.current?.click()}
              >
                {ctrlImgPreview ? (
                  <img src={ctrlImgPreview} alt="Preview" className="w-full max-h-40 object-contain rounded-lg" />
                ) : (
                  <><Image size={32} className="text-gray-300" /><span className="text-sm text-gray-400">Haz clic para seleccionar imagen</span></>
                )}
                {ctrlImgPreview && <span className="text-xs text-blue-600 hover:underline">Cambiar imagen</span>}
              </div>
              <input ref={ctrlImgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setCtrlImgFile(f); setCtrlImgPreview(URL.createObjectURL(f)); }
              }} />
              <div className="flex gap-3 justify-end pt-1">
                <button type="button" onClick={() => setShowCtrlImgModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={ctrlUploadMutation.isPending || !ctrlImgFile} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
                  {ctrlUploadMutation.isPending ? 'Subiendo...' : 'Guardar imagen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">
              {editingId ? 'Editar Gabinete' : 'Nuevo Gabinete'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                  <input type="text" value={form.codigo} onChange={(e) => updateField('codigo', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input type="text" value={form.nombre} onChange={(e) => updateField('nombre', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select value={form.tipo} onChange={(e) => updateField('tipo', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="INDOOR">Indoor</option>
                    <option value="OUTDOOR">Outdoor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pixel Pitch (mm)</label>
                  <input type="number" step="0.01" value={form.pixelPitch || ''} onChange={(e) => updateField('pixelPitch', parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ancho (cm)</label>
                  <input type="number" step="0.01" value={form.anchoCm || ''} onChange={(e) => updateField('anchoCm', parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alto (cm)</label>
                  <input type="number" step="0.01" value={form.altoCm || ''} onChange={(e) => updateField('altoCm', parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profundidad (mm)</label>
                  <input type="number" step="0.01" value={form.profundidadMm || ''} onChange={(e) => updateField('profundidadMm', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Peso (kg)</label>
                  <input type="number" step="0.01" value={form.pesoKg || ''} onChange={(e) => updateField('pesoKg', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo LED</label>
                  <input type="text" value={form.ledType || ''} onChange={(e) => updateField('ledType', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resolución Gabinete</label>
                  <input type="text" value={form.resolucionGabinete || ''} onChange={(e) => updateField('resolucionGabinete', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" placeholder="ej: 192x192" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brillo (nits)</label>
                  <input type="number" value={form.brilloNits || ''} onChange={(e) => updateField('brilloNits', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Consumo Máx (W)</label>
                  <input type="number" step="0.01" value={form.consumoMaxW || ''} onChange={(e) => updateField('consumoMaxW', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Consumo Prom (W)</label>
                  <input type="number" step="0.01" value={form.consumoPromW || ''} onChange={(e) => updateField('consumoPromW', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Rate (Hz)</label>
                  <input type="number" value={form.refreshRate || ''} onChange={(e) => updateField('refreshRate', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vida Útil (hrs)</label>
                  <input type="number" value={form.vidaUtil || ''} onChange={(e) => updateField('vidaUtil', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Protección IP</label>
                  <input type="text" value={form.proteccionIp || ''} onChange={(e) => updateField('proteccionIp', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" placeholder="ej: IP65" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" disabled={createMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  {createMutation.isPending ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
